/**
 * Insights Page.
 *
 * Business intelligence dashboard showing algorithmic pricing recommendations,
 * slow-moving inventory alerts, demand forecasts, and inventory turnover.
 * Optionally includes an AI-powered narrative summary.
 */

import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  DollarSign,
  BarChart2,
  Lightbulb,
} from "lucide-react";
import {
  useSlowMovers,
  useDemandForecast,
  useTurnover,
  useInsightsSummary,
} from "@/features/insights/api";
import Badge from "@/components/ui/Badge";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );
const trendIcon = (t: string) =>
  t === "up" ? (
    <TrendingUp className="h-4 w-4 text-[var(--text-success)]" />
  ) : t === "down" ? (
    <TrendingDown className="h-4 w-4 text-[var(--text-danger)]" />
  ) : (
    <Minus className="h-4 w-4 text-[var(--text-tertiary)]" />
  );

function InsightsPage() {
  const navigate = useNavigate();
  const { data: summary, isLoading: summaryLoading } = useInsightsSummary();
  const { data: slowMovers, isLoading: smLoading } = useSlowMovers();
  const { data: forecast, isLoading: fcLoading } = useDemandForecast();
  const { data: turnover, isLoading: toLoading } = useTurnover();

  const isLoading = summaryLoading || smLoading || fcLoading || toLoading;

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Insights
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Algorithmic business intelligence — no AI costs incurred.
        </p>
      </div>

      {/* AI Summary (if available) */}
      {summary?.narrative && (
        <Card className="border-[var(--fill-info)] bg-[var(--fill-info)]">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-[var(--color-interactive-primary)] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">AI Summary</p>
              <p className="mt-1 text-sm text-blue-800">{summary.narrative}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-yellow-100">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">
                Slow Movers
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {slowMovers?.total_slow_movers || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--fill-info)]">
              <BarChart2 className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">Turn Rate</p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {turnover?.turn_rate || 0}x
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--fill-success)]">
              <DollarSign className="h-5 w-5 text-[var(--text-success)]" />
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">
                Avg Days to Sell
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {turnover?.days_to_sell ?? "—"}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Slow Movers */}
        <Card padding="none">
          <div className="border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Slow-Moving Inventory
            </h3>
            <p className="text-xs text-[var(--text-tertiary)]">
              Assets exceeding 2x category avg days
            </p>
          </div>
          {slowMovers && slowMovers.items.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {slowMovers.items.map((item) => (
                <div
                  key={item.asset_id}
                  onClick={() => navigate(`/inventory/${item.asset_id}`)}
                  className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3 cursor-pointer hover:bg-[var(--surface-sunken)]"
                >
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {item.name}
                    </p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {item.sku} &middot; {item.days_in_inventory}d (avg{" "}
                      {item.category_avg_days}d)
                    </p>
                  </div>
                  <Badge variant="warning">
                    -{item.suggested_reduction_pct}%
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
              No slow-moving inventory detected.
            </p>
          )}
        </Card>

        {/* Demand Forecast */}
        <Card padding="none">
          <div className="border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Demand Forecast
            </h3>
            <p className="text-xs text-[var(--text-tertiary)]">
              Weighted moving average by category
            </p>
          </div>
          {forecast && forecast.forecasts.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-center">Monthly Avg</th>
                  <th className="px-4 py-2 text-center">Next 3mo</th>
                  <th className="px-4 py-2 text-center">Trend</th>
                </tr>
              </thead>
              <tbody>
                {forecast.forecasts.map((f) => (
                  <tr
                    key={f.category}
                    className="border-b border-[var(--border-subtle)]"
                  >
                    <td className="px-4 py-2 capitalize font-medium">
                      {f.category}
                    </td>
                    <td className="px-4 py-2 text-center">{f.monthly_avg}</td>
                    <td className="px-4 py-2 text-center font-medium">
                      {f.projected_next_3_months}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {trendIcon(f.trend)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
              Not enough sales data for forecasting yet.
            </p>
          )}
        </Card>

        {/* Turnover by Category */}
        <Card padding="none" className="lg:col-span-2">
          <div className="border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Inventory Turnover by Category
            </h3>
            <p className="text-xs text-[var(--text-tertiary)]">
              {turnover?.period_months || 12}-month period
            </p>
          </div>
          {turnover && turnover.by_category.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-right">Units Sold</th>
                  <th className="px-4 py-2 text-right">COGS Sold</th>
                  <th className="px-4 py-2 text-right">Turn Rate</th>
                </tr>
              </thead>
              <tbody>
                {turnover.by_category.map((c) => (
                  <tr
                    key={c.category}
                    className="border-b border-[var(--border-subtle)]"
                  >
                    <td className="px-4 py-2 capitalize font-medium">
                      {c.category}
                    </td>
                    <td className="px-4 py-2 text-right">{c.units_sold}</td>
                    <td className="px-4 py-2 text-right">{fmt(c.cogs_sold)}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      {c.turn_rate}x
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
              No sales data for turnover analysis yet.
            </p>
          )}
        </Card>
      </div>

      {/* Method Note */}
      <p className="text-center text-xs text-[var(--text-tertiary)]">
        All insights use statistical algorithms (weighted moving averages,
        threshold analysis, accounting formulas).
        {summary?.ai_enabled
          ? " AI narrative powered by your configured provider."
          : " No AI provider configured — enable in .env for narrative summaries."}
      </p>
    </div>
  );
}

export default InsightsPage;
